import { leaseUpYearShare } from './leaseUp';
import { grownExpenses } from './expenseGrowth';
import { servicingFeeForYear, type FinancingFeeEffect } from './financingFee';
/**
 * Financial Calculations for CRE Deal Analysis
 * CCIM-level formulas for income, expenses, debt, and returns
 */

// ============================================================================
// Income Calculations
// ============================================================================

export function calculateVacancyAmount(pgi: number, vacancyRate: number): number {
  return pgi * (vacancyRate / 100);
}

export function calculateEffectiveGrossIncome(
  pgi: number,
  vacancyAmount: number,
  otherIncome: number = 0
): number {
  return pgi - vacancyAmount + otherIncome;
}

// ============================================================================
// Expense Calculations
// ============================================================================

export function calculateManagementFee(egi: number, feePercent: number): number {
  return egi * (feePercent / 100);
}

export function calculateTotalExpenses(expenses: {
  propertyTaxes?: number;
  insurance?: number;
  utilities?: number;
  managementFee?: number;
  repairsMaintenance?: number;
  reservesCapex?: number;
  otherExpenses?: number;
}): number {
  return (
    (expenses.propertyTaxes || 0) +
    (expenses.insurance || 0) +
    (expenses.utilities || 0) +
    (expenses.managementFee || 0) +
    (expenses.repairsMaintenance || 0) +
    (expenses.reservesCapex || 0) +
    (expenses.otherExpenses || 0)
  );
}

export function calculateOperatingExpenseRatio(
  totalExpenses: number,
  egi: number
): number {
  if (egi === 0) return 0;
  return (totalExpenses / egi) * 100;
}

// ============================================================================
// NOI & Valuation
// ============================================================================

export function calculateNOI(egi: number, totalExpenses: number): number {
  return egi - totalExpenses;
}

export function calculateCapRate(noi: number, purchasePrice: number): number {
  if (purchasePrice === 0) return 0;
  return (noi / purchasePrice) * 100;
}

export function calculateValueFromCapRate(noi: number, capRate: number): number {
  if (capRate === 0) return 0;
  return noi / (capRate / 100);
}

export function calculatePricePerSqft(
  purchasePrice: number,
  squareFootage: number
): number {
  if (squareFootage === 0) return 0;
  return purchasePrice / squareFootage;
}

export function calculateGRM(purchasePrice: number, annualGrossIncome: number): number {
  if (annualGrossIncome === 0) return 0;
  return purchasePrice / annualGrossIncome;
}

// ============================================================================
// Lease Type / NNN Reimbursements
// ============================================================================

/**
 * Calculate NNN expense reimbursements from tenants
 * NNN tenants reimburse taxes + insurance + maintenance at a recovery rate
 */
export function calculateNNNReimbursements(
  taxes: number,
  insurance: number,
  maintenance: number,
  recoveryRate: number
): number {
  return (taxes + insurance + maintenance) * (recoveryRate / 100);
}

/**
 * ⭐⭐⭐ WHO REIMBURSES A CARRY? NOBODY - and that is why this resolver exists.
 *
 * A recovery is a TENANT paying back an expense the landlord fronted. `expensesAreCarry` says the
 * asset earns nothing, which means it has no tenants, which means there is no one to reimburse
 * anything. Left unsuppressed the two settings contradict each other and the contradiction is
 * INVISIBLE: driving the San Jacinto land deal in, a 55.92% recovery rate left over from the host
 * property reimbursed 55.92% of the $41,975 tax line and the screen printed **$23,472 of income
 * on a parcel with none.**
 *
 * ⛔ IT REPORTS THAT IT SUPPRESSED SOMETHING RATHER THAN QUIETLY RETURNING 0. Silently zeroing a
 * rate the user typed is how a screen comes to disagree with the field beside it - the defect
 * class this build produces most. The caller shows the suppression; the rate itself is left
 * stored, so turning the carry flag off restores it untouched.
 *
 * ⭐ Both recovery shapes are suppressed. A modified-gross reimbursement is exactly as incoherent
 * on a non-income asset as a triple-net one.
 */
export function resolveExpenseRecoveries(params: {
  leaseType: string | null | undefined;
  propertyTaxes: number;
  insurance: number;
  repairsMaintenance: number;
  nnnRecoveryRate: number;
  modifiedGrossReimbursement: number;
  /** The asset earns nothing, so nothing can be recovered from a tenant it does not have. */
  expensesAreCarry?: boolean;
}): { recoveries: number; suppressedByCarry: boolean } {
  const stated = params.leaseType === 'nnn'
    ? calculateNNNReimbursements(
        params.propertyTaxes, params.insurance,
        params.repairsMaintenance, params.nnnRecoveryRate)
    : params.leaseType === 'modified_gross'
      ? params.modifiedGrossReimbursement
      : 0;

  if (params.expensesAreCarry && stated > 0) {
    return { recoveries: 0, suppressedByCarry: true };
  }
  return { recoveries: stated, suppressedByCarry: false };
}

/**
 * Calculate total annual debt service combining bank + seller carryback
 */
export function calculateTotalAnnualDebtService(
  bankMonthlyPayment: number,
  sellerMonthlyPayment: number
): number {
  return (bankMonthlyPayment + sellerMonthlyPayment) * 12;
}

/**
 * Calculate adjusted down payment accounting for seller carryback
 */
export function calculateAdjustedDownPayment(
  purchasePrice: number,
  bankLoan: number,
  sellerCarryback: number
): number {
  return Math.max(0, purchasePrice - bankLoan - sellerCarryback);
}

// ============================================================================
// Debt Analysis
// ============================================================================

export function calculateLoanAmount(purchasePrice: number, ltvPercent: number): number {
  return purchasePrice * (ltvPercent / 100);
}

export function calculateDownPayment(purchasePrice: number, loanAmount: number): number {
  return purchasePrice - loanAmount;
}

/**
 * Equity-first sizing: the investor states the cash they will place and the
 * loan is whatever remains. LTV becomes an OUTPUT, not an input.
 * This is how every delivered analysis in the reference corpus is structured
 * ("Bank Loan (34.8% LTV - sized to keep $3.0M cash equity)").
 *
 * `down_payment_target` is a nullable column, so a missing, non-finite or
 * negative equity figure is a normal state meaning "no equity stated" - it
 * contributes 0, never NaN. The loan is clamped to the purchase price so the
 * returned LTV can never exceed 100%.
 */
export function sizeLoanFromEquity(
  purchasePrice: number,
  downPaymentTarget: number | null | undefined,
  sellerCarryback: number | null | undefined = 0
): { loanAmount: number; ltvPercent: number } {
  if (!isValidNumber(purchasePrice) || purchasePrice <= 0) {
    return { loanAmount: 0, ltvPercent: 0 };
  }
  const equity = isValidNumber(downPaymentTarget) && downPaymentTarget > 0 ? downPaymentTarget : 0;
  const carryback = isValidNumber(sellerCarryback) && sellerCarryback > 0 ? sellerCarryback : 0;
  const loanAmount = Math.min(purchasePrice, Math.max(0, purchasePrice - equity - carryback));
  return { loanAmount, ltvPercent: (loanAmount / purchasePrice) * 100 };
}

/**
 * Calculate monthly mortgage payment using PMT formula
 * PMT = P * (r * (1+r)^n) / ((1+r)^n - 1)
 */
export function calculateMonthlyPayment(
  loanAmount: number,
  annualInterestRate: number,
  amortizationYears: number
): number {
  if (loanAmount === 0 || annualInterestRate === 0 || amortizationYears === 0) {
    return 0;
  }

  const monthlyRate = annualInterestRate / 100 / 12;
  const numPayments = amortizationYears * 12;

  const payment =
    loanAmount *
    (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) /
    (Math.pow(1 + monthlyRate, numPayments) - 1);

  return isFinite(payment) ? payment : 0;
}

export function calculateAnnualDebtService(monthlyPayment: number): number {
  return monthlyPayment * 12;
}

/**
 * Present value of an annuity. The inverse of calculateMonthlyPayment:
 * given a payment a borrower can afford, how much principal does it support?
 */
export function pvOfAnnuity(
  monthlyPayment: number,
  annualRatePercent: number,
  years: number
): number {
  if (monthlyPayment <= 0 || years <= 0) return 0;
  const r = annualRatePercent / 100 / 12;
  const n = years * 12;
  if (r === 0) return monthlyPayment * n;
  return (monthlyPayment * (1 - Math.pow(1 + r, -n))) / r;
}

/**
 * Debt yield = NOI / loan. Lender metric that is immune to both the cap rate
 * and the interest rate, which is why it is the first number a debt broker asks for.
 */
export function calculateDebtYield(noi: number, loanAmount: number): number {
  if (loanAmount <= 0) return 0;
  return (noi / loanAmount) * 100;
}

export interface LoanSizingParams {
  purchasePrice: number;
  noi: number;
  /**
   * Covenants and loan terms. Each may legitimately be absent - a term sheet
   * that quotes no debt yield floor, or an interest-only quote with no
   * amortization. A missing, null, zero or non-finite value DISABLES its test;
   * it never caps the loan at zero.
   */
  maxLtvPercent?: number | null;
  minDscr?: number | null;
  minDebtYield?: number | null;
  interestRate?: number | null;
  amortizationYears?: number | null;
}

export interface LoanSizingResult {
  /** A disabled test reports Infinity - it places no ceiling on the loan. */
  byLtv: number;
  byDscr: number;
  byDebtYield: number;
  maxLoan: number;
  constraint: 'ltv' | 'dscr' | 'debt_yield' | 'none';
}

/**
 * A lender sizes to the MINIMUM of three tests. Asserting an LTV without
 * checking DSCR and debt yield produces loan amounts no lender would fund.
 *
 * A test that cannot be evaluated contributes Infinity rather than 0, so an
 * unquoted covenant drops out of the minimum instead of collapsing the whole
 * sizing to $0 and mislabelling itself as the binding constraint. If every
 * test is disabled there is nothing to size against, so the result is $0 with
 * constraint 'none' - the same shape as a non-positive NOI.
 */
export function maxSupportableLoan(p: LoanSizingParams): LoanSizingResult {
  const DISABLED = Infinity;

  const noi = isValidNumber(p.noi) ? p.noi : 0;
  const price = isValidNumber(p.purchasePrice) ? p.purchasePrice : 0;
  const maxLtvPercent = isValidNumber(p.maxLtvPercent) ? p.maxLtvPercent : 0;
  const minDscr = isValidNumber(p.minDscr) ? p.minDscr : 0;
  const minDebtYield = isValidNumber(p.minDebtYield) ? p.minDebtYield : 0;
  const amortizationYears = isValidNumber(p.amortizationYears) ? p.amortizationYears : 0;
  // A 0% rate is a real (if rare) quote, so only a non-finite rate disables the
  // DSCR test; pvOfAnnuity handles r === 0 as plain multiplication.
  const interestRate = isValidNumber(p.interestRate) ? p.interestRate : null;

  const byLtv = maxLtvPercent > 0 && price > 0 ? price * (maxLtvPercent / 100) : DISABLED;
  const byDscr =
    noi > 0 && minDscr > 0 && interestRate !== null && amortizationYears > 0
      ? pvOfAnnuity(noi / minDscr / 12, interestRate, amortizationYears)
      : DISABLED;
  const byDebtYield = noi > 0 && minDebtYield > 0 ? noi / (minDebtYield / 100) : DISABLED;

  if (noi <= 0) {
    return { byLtv, byDscr, byDebtYield, maxLoan: 0, constraint: 'none' };
  }

  const maxLoan = Math.min(byLtv, byDscr, byDebtYield);
  if (!isValidNumber(maxLoan)) {
    return { byLtv, byDscr, byDebtYield, maxLoan: 0, constraint: 'none' };
  }

  const constraint: LoanSizingResult['constraint'] =
    maxLoan === byDscr ? 'dscr' : maxLoan === byDebtYield ? 'debt_yield' : 'ltv';

  return { byLtv, byDscr, byDebtYield, maxLoan, constraint };
}

export function calculateDSCR(noi: number, annualDebtService: number): number {
  if (annualDebtService === 0) return 0;
  return noi / annualDebtService;
}

// ============================================================================
// Cash Flow & Returns
// ============================================================================

export function calculateClosingCosts(
  purchasePrice: number,
  closingCostPercent: number
): number {
  return purchasePrice * (closingCostPercent / 100);
}

/**
 * ⭐⭐ SOURCES EQUAL USES, and this is where the identity is struck:
 *
 *     uses    = purchase price + closing costs + financing fee
 *     sources = primary note + secondary note + cash
 *
 * ⛔ THE THIRD ARGUMENT IS THE FEE PAID IN **CASH**, never the whole fee. A FINANCED fee has a
 * matching source - the note that carries it - so it costs no cash and the down payment does not
 * move either, because the extra loan proceeds pay the fee rather than the seller. That is
 * exactly "raises the NOTE, not the cash". Pass `FinancingFeeEffect.cashCost` and both mechanics
 * are right by construction; pass `.fee` and a financed 504 double-counts $67,000.
 *
 * 🚨 A THIRD ARGUMENT RATHER THAN FOUR CALL SITES ADDING A TERM THEMSELVES. Every pricing path
 * already calls this one function; making each remember to add it is how one deal comes to be
 * priced two ways, which is the defect class this build produces most.
 * ⛔ It defaults to 0, so every existing call and every saved analysis is untouched.
 * ▶ `financingFee.ts`.
 */
export function calculateTotalCashRequired(
  downPayment: number,
  closingCosts: number,
  financingFeeCashCost: number = 0
): number {
  return downPayment + closingCosts + (isValidNumber(financingFeeCashCost) ? financingFeeCashCost : 0);
}

export function calculateBeforeTaxCashFlow(
  noi: number,
  annualDebtService: number
): number {
  return noi - annualDebtService;
}

export function calculateCashOnCash(
  annualCashFlow: number,
  totalCashInvested: number
): number {
  if (totalCashInvested === 0) return 0;
  return (annualCashFlow / totalCashInvested) * 100;
}

// ============================================================================
// Validation Helpers
// ============================================================================

export function isValidNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value) && isFinite(value);
}

export function parseNumberInput(value: string | number | undefined): number {
  if (value === undefined || value === '') return 0;
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return isValidNumber(parsed) ? parsed : 0;
}

// ============================================================================
// Warning Thresholds
// ============================================================================

export const THRESHOLDS = {
  DSCR_WARNING: 1.25,      // Below this is risky
  DSCR_GOOD: 1.5,          // Above this is good
  CAP_RATE_LOW: 4.0,       // Below this might be overpriced
  CAP_RATE_HIGH: 8.0,      // Above this might be risky
  EXPENSE_RATIO_HIGH: 45,  // Above this is high expenses
  COC_GOOD: 8.0,           // Above this is good return
};

export function getDSCRStatus(dscr: number): 'danger' | 'warning' | 'good' {
  if (dscr < THRESHOLDS.DSCR_WARNING) return 'danger';
  if (dscr < THRESHOLDS.DSCR_GOOD) return 'warning';
  return 'good';
}

export function getCapRateStatus(capRate: number): 'low' | 'normal' | 'high' {
  if (capRate < THRESHOLDS.CAP_RATE_LOW) return 'low';
  if (capRate > THRESHOLDS.CAP_RATE_HIGH) return 'high';
  return 'normal';
}

// ============================================================================
// Formatting Helpers
// ============================================================================

export function formatCurrency(value: number | undefined | null): string {
  if (value === null || value === undefined || !isFinite(value)) return '$0';
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

export function formatPercent(value: number | undefined | null, decimals = 2): string {
  if (value === null || value === undefined || !isFinite(value)) return '0%';
  return `${value.toFixed(decimals)}%`;
}

export function formatNumber(value: number | undefined | null): string {
  if (value === null || value === undefined || !isFinite(value)) return '0';
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export function formatRatio(value: number | undefined | null): string {
  if (value === null || value === undefined || !isFinite(value)) return '0.00x';
  return `${value.toFixed(2)}x`;
}

// ============================================================================
// Multi-Year Projections
// ============================================================================

export interface YearProjection {
  year: number;
  income: number;
  expenses: number;
  noi: number;
  debtService: number;
  cashFlow: number;
  /** ALL debt outstanding - what the exit waterfall retires. */
  loanBalance: number;
  /**
   * ⭐ The two notes, exposed SEPARATELY so no surface has to re-derive one from the other.
   * `bankLoanBalance + secondaryLoanBalance === loanBalance`, pinned in `secondNote.test.ts`.
   * The exit waterfall prints one line each, and they sum to the net it reports.
   */
  bankLoanBalance: number;
  secondaryLoanBalance: number;
  /**
   * ⭐⭐ The ONGOING financing fee charged this year - the SBA 7(a) service fee and its like.
   * Already deducted from `cashFlow` above and deliberately absent from `debtService`, because
   * Joseph strikes DSCR on debt service alone while his cash flow line carries the fee. Exposed
   * so a surface can PRINT the line rather than re-derive it; a deduction nothing can show is
   * a number that appears from nowhere. ▶ `financingFee.ts`.
   */
  servicingFee: number;
  /**
   * ⭐⭐ CARRY on a non-income asset - property tax, weed abatement, fencing, insurance and
   * administration on land that earns nothing. Already deducted from `cashFlow` and deliberately
   * ABSENT from `expenses` and from `noi`, because a carry is CAPITAL, not an operating expense.
   * Exposed so a surface can PRINT the line: a deduction nothing can show is a number that
   * appears from nowhere, which is why `servicingFee` above is exposed too.
   */
  carryCosts: number;
  equity: number;
}

/**
 * A contractual NOI schedule, index 0 = year 1. Stored as jsonb, so it round-
 * trips with holes: `[304800, null, 310500]` is a legitimate shape meaning
 * "year 2 has no contractual figure, model it". Only finite numbers are
 * treated as overrides.
 */
export type NoiSchedule = Array<number | null | undefined>;

export interface ProjectionInputs {
  initialIncome: number;
  initialExpenses: number;
  incomeGrowthRate: number; // percentage
  expenseGrowthRate: number; // percentage
  purchasePrice: number;
  loanAmount: number;
  interestRate: number; // percentage
  amortizationYears: number;
  holdingPeriod: number; // years
  exitCapRate: number; // percentage
  sellingCosts: number; // percentage
  /**
   * ⭐⭐⭐ A SECOND NOTE - a seller carryback, or the CDC debenture in an SBA 504.
   *
   * ⛔ ITS PAYMENT AND ITS BALANCE BOTH DERIVE FROM THIS ONE OBJECT, and that is the point. The
   * field this replaces was `additionalAnnualDebtService`, a pre-computed payment passed with no
   * balance inputs beside it - so the note was SERVICED and never RETIRED. `loanBalance` carried
   * the bank loan alone and the exit paid off one note of two. On the Victorville SBA 504 that
   * reported $2,549,672 of net equity against a real $422,817. **Passing a derived figure
   * alongside the inputs it was derived from is how the two came apart.**
   */
  secondaryLoan?: SecondaryNote;
  /**
   * @deprecated PAYMENT ONLY - services a second note and CANNOT retire one, because a payment
   * carries no terms to amortize. Ignored whenever `secondaryLoan` is present. It exists so the
   * pinned golden and scenario tests compile unchanged; **no live path uses it.**
   */
  legacySecondaryAnnualDS?: number;
  /**
   * Explicit contractual NOI by year, index 0 = year 1. When entry i is a
   * finite number it replaces the growth-rate-derived NOI for that year; a
   * null, undefined or NaN entry falls back to the modelled NOI. This is the
   * seam the Phase 3 monthly lease engine feeds; until then it is set by hand
   * for deals with dated rent steps.
   */
  // The row stores the schedule as jsonb, so NULL is a value it can hold; the loop treats it as absent.
  noiOverrides?: NoiSchedule | null;
  /**
   * Leasing capital by year, index 0 = year 1. Phase 3B feeds TI and LC here.
   *
   * This reduces CASH FLOW and never NOI. TI/LC is capex, not an operating expense:
   * netting it against NOI would understate NOI, and since the exit prices off NOI it
   * would also silently cut the sale price. Both errors compound in the same direction.
   */
  capitalCostsByYear?: number[];
  /**
   * ⭐⭐ Months from the close during which the building produces NO income - a vacant asset being
   * leased up. Absent or 0 is exactly the previous behaviour and every existing deal is untouched.
   *
   * 🚨 `va_vacancy_leaseup` was a CHECKBOX read by nothing, so the archetype named
   * "Value-add / lease-up" priced a vacant box as though it stabilised on the day of close - 3.4
   * points of IRR too flattering on Jackson Mobility. ▶ `leaseUp.ts` carries the measurement.
   *
   * ⛔ It suspends INCOME AND ITS OPERATING EXPENSES together, so the lease-up year is NOI $0
   * rather than a negative. An empty building's real carry - taxes, insurance, debt - is a
   * CAPITAL cost and belongs in `capitalCostsByYear` / the Carry Costs field, which is where
   * Joseph's own $250,000 reserve sits. The pre-flight receipt flags a lease-up with no carry.
   */
  leaseUpMonths?: number;
  /**
   * The property-tax portion of `initialExpenses`, so it can grow at its own rate.
   * ⭐ Prop 13 caps assessed-value growth at 2% while other lines run with inflation. ▶ `expenseGrowth.ts`.
   */
  propertyTaxes?: number;
  /** Annual tax growth as a PERCENTAGE. Absent means "same as expenses" - the previous behaviour. */
  taxGrowthRate?: number | null;
  /**
   * ⭐⭐ THE ONGOING FINANCING FEE - a percent per year of the OUTSTANDING balance at the START of
   * each year, across ALL notes. The SBA 7(a)'s service fee is the specimen: $20,625 in year 1 on
   * the full $5,000,000 note, declining to $19,528 in year 5 as the balance amortizes.
   *
   * ⛔ IT REDUCES CASH FLOW AND NOTHING ELSE. It is not debt service, so DSCR is untouched -
   * Joseph's own 7(a) prints DSCR 0.66x struck on debt service alone while his cash flow line
   * carries the fee, and getting that backwards would move a covenant test. It is not an
   * operating expense either, so NOI is untouched and the exit price cannot drift with it.
   *
   * ⛔ NULL/absent is exactly the previous arithmetic. ▶ `financingFee.ts`.
   */
  servicingFeeRate?: number | null;
  /**
   * ⭐⭐⭐ THE ASSET EARNS NOTHING, SO WHAT WOULD BE OPERATING EXPENSES ARE A CARRY.
   *
   * ⛔ A ROUTING FLAG, NOT A NEW COST. The expense fields and their growth rates already describe
   * the carry exactly - Joseph's San Jacinto land runs property tax at 1.15% of basis growing 2%
   * under Prop 13 and $6,000 of abatement, fencing, insurance and administration growing 3%, and
   * `grownExpenses` already splits those two rates and reproduces all five of his years to the
   * dollar. **All that was missing was where the money goes.**
   *
   * ⛔ WHY IT MATTERS: charged as an operating expense against zero income, NOI goes NEGATIVE, a
   * negative NOI over a cap rate is a NEGATIVE EXIT, no cash flow is positive and `calculateIRR`
   * returns its `-100` sentinel for the screen to print as a measurement. That is the Victorville
   * defect exactly. Charged as CARRY, NOI is 0, the exit is priced on its own basis, and the cost
   * falls where it actually falls.
   * ⭐ Joseph's own Jackson ruling generalised: *income and its operating expenses suspend
   * together, so the vacant year is NOI $0 and the carry is CAPITAL.* Land is that, permanently.
   *
   * ⛔ THE CARRY IS NOT SCALED BY `occupiedShare`. A carry is precisely what you pay while the
   * asset is EMPTY, so suspending it during a lease-up would delete it at the one time it is
   * certainly being paid.
   */
  expensesAreCarry?: boolean;
}

/**
 * Calculate remaining loan balance after n years
 * Using the loan amortization formula
 */
export function calculateLoanBalance(
  originalLoan: number,
  annualRate: number,
  amortizationYears: number,
  yearsElapsed: number
): number {
  if (originalLoan === 0 || annualRate === 0) return originalLoan;

  const monthlyRate = annualRate / 100 / 12;
  const totalPayments = amortizationYears * 12;
  const paymentsMade = yearsElapsed * 12;

  // Remaining balance formula
  const remainingBalance = originalLoan *
    (Math.pow(1 + monthlyRate, totalPayments) - Math.pow(1 + monthlyRate, paymentsMade)) /
    (Math.pow(1 + monthlyRate, totalPayments) - 1);

  return isFinite(remainingBalance) ? Math.max(0, remainingBalance) : 0;
}

/**
 * ⭐⭐⭐ A SECOND NOTE, AND THE TWO NUMBERS IT PRODUCES.
 *
 * ⛔ ITS PAYMENT AND ITS BALANCE BOTH DERIVE FROM THIS ONE OBJECT, through the two functions
 * below, and no surface computes either itself. That is the discipline the seller-carryback
 * defect bought: `additionalAnnualDebtService` was a pre-computed PAYMENT with no balance beside
 * it, so the note was SERVICED and never RETIRED, and the exit paid off one note of two.
 */
export interface SecondaryNote {
  amount: number;
  interestRate: number;      // percentage
  amortizationYears: number;
  /**
   * ⭐⭐ INTEREST-ONLY: level interest, principal CONSTANT, the whole balance due at exit.
   *
   * A shape the amortizing slot cannot approximate. Joseph's San Jacinto land analysis models a
   * $650,000 carryback at 7.50% interest-only: $48,750 of interest in every one of the five years
   * and $650,000 still outstanding at the sale. Forced through a 25-year amortization the same
   * note pays $57,641 a year (**$8,891 too much, every year**) and stands at $596,262 at exit
   * (**$53,738 too little**); only an absurd 1,000-year amortization converges on it.
   *
   * ⛔ It is the natural shape for land and for a seller carryback, because a non-income asset
   * has nothing to amortize principal out of. `amortizationYears` is IGNORED when this is set -
   * the balloon is the whole balance, and the hold's final year is when it comes due.
   */
  interestOnly?: boolean;
}

/** The annual debt service on a second note. Interest-only pays interest and nothing else. */
export function secondaryNoteAnnualDebtService(note: SecondaryNote): number {
  if (!isValidNumber(note.amount) || note.amount <= 0) return 0;
  if (note.interestOnly) return note.amount * (note.interestRate / 100);
  return calculateAnnualDebtService(
    calculateMonthlyPayment(note.amount, note.interestRate, note.amortizationYears));
}

/**
 * The outstanding balance on a second note after `yearsElapsed` years.
 * ⛔ An interest-only note amortizes NOTHING, so this is the original principal in every year -
 * which is exactly why the exit must retire the whole of it.
 */
export function secondaryNoteBalance(note: SecondaryNote, yearsElapsed: number): number {
  if (!isValidNumber(note.amount) || note.amount <= 0) return 0;
  if (note.interestOnly) return note.amount;
  return calculateLoanBalance(note.amount, note.interestRate, note.amortizationYears, yearsElapsed);
}

/** The inputs that decide what a hold year EARNS - everything in `ProjectionInputs` that is not debt or exit. */
export type NoiProjectionInputs = Pick<ProjectionInputs,
  | 'initialIncome' | 'initialExpenses' | 'incomeGrowthRate' | 'expenseGrowthRate'
  | 'noiOverrides' | 'leaseUpMonths' | 'propertyTaxes' | 'taxGrowthRate' | 'expensesAreCarry'>;

/** What one hold year earns, before debt and capital. */
export interface NoiYear {
  income: number;
  expenses: number;
  noi: number;
  /** The same figures as `expenses` when they are a CARRY - reduces cash flow, never NOI. */
  carryCosts: number;
}

/**
 * ⛔ THE ONLY hand-written list of the NOI fields inside the engine. `computeScenario` and
 * `computeDealIRR` pick their projection inputs through it, so neither can leave a field out the
 * way both once left out the lease-up and Prop 13.
 */
export const pickNoiInputs = (x: NoiProjectionInputs): NoiProjectionInputs => ({
  initialIncome: x.initialIncome,
  initialExpenses: x.initialExpenses,
  incomeGrowthRate: x.incomeGrowthRate,
  expenseGrowthRate: x.expenseGrowthRate,
  noiOverrides: x.noiOverrides,
  leaseUpMonths: x.leaseUpMonths,
  propertyTaxes: x.propertyTaxes,
  taxGrowthRate: x.taxGrowthRate,
  expensesAreCarry: x.expensesAreCarry,
});

/**
 * ⭐⭐⭐ WHAT ONE HOLD YEAR EARNS - THE RULE, IN ONE PLACE (JJ2, 2026-09-02).
 *
 * `generateProjections` runs this for every year, and `yearOneNoi` runs it for year 1, so the
 * strip, the scorecard, the sizing, the exit fallback and the proforma table cannot disagree
 * about the same year of the same deal. Before it existed the screen struck its headline NOI as
 * `egi - totalExpenses` BEFORE the carry or the lease-up was read: the untouched Victorville
 * screen printed NOI -$100,443, cap -1.75% and DSCR -0.29x beside a proforma reading NOI $0.
 *
 * ⛔ Behaviour-preserving extraction. Every line here is the loop body it came from.
 */
export function projectNoiYear(inputs: NoiProjectionInputs, year: number): NoiYear {
  /**
   * ⭐ The growth clock starts at STABILISATION, not at the close - see `leaseUp.ts`. With no
   * lease-up this returns `{ occupiedShare: 1, growthPeriods: year - 1 }`, which is precisely
   * the arithmetic that was here before, so nothing without a lease-up moves.
   */
  const { occupiedShare, growthPeriods } = leaseUpYearShare(year, inputs.leaseUpMonths ?? 0);
  let income = inputs.initialIncome * Math.pow(1 + inputs.incomeGrowthRate / 100, growthPeriods) * occupiedShare;
  const grownThisYear = grownExpenses({
    totalExpenses: inputs.initialExpenses,
    propertyTaxes: inputs.propertyTaxes ?? 0,
    expenseGrowthRate: inputs.expenseGrowthRate / 100,
    taxGrowthRate: typeof inputs.taxGrowthRate === 'number' ? inputs.taxGrowthRate / 100 : null,
    periods: growthPeriods,
  });
  /**
   * ⛔ ONE FIGURE, TWO DESTINATIONS. Either it is an operating expense and reduces NOI, or it is
   * a CARRY and reduces cash flow only - never both and never neither, so the total cost is
   * identical either way and only the NOI moves. ⭐ The carry ignores `occupiedShare`: it is
   * what you pay while the asset is empty.
   */
  const carryCosts = inputs.expensesAreCarry ? grownThisYear : 0;
  const expenses = inputs.expensesAreCarry ? 0 : grownThisYear * occupiedShare;
  let noi = income - expenses;
  // Contractual rent steps win over modeled growth. Income is back-solved from
  // the contractual NOI so that income - expenses === noi; without this the
  // proforma table renders a growth-derived income beside an overridden NOI
  // and the arithmetic visibly fails in front of a client.
  // Only a finite number is an override: a `!== undefined` check let a jsonb
  // null through and made it the NOI itself.
  const override = inputs.noiOverrides?.[year - 1];
  if (isValidNumber(override)) {
    noi = override;
    income = noi + expenses;
  }
  return { income, expenses, noi, carryCosts };
}

/**
 * Generate year-by-year projections
 */
export function generateProjections(inputs: ProjectionInputs): YearProjection[] {
  const {
    incomeGrowthRate,
    purchasePrice,
    loanAmount,
    interestRate,
    amortizationYears,
    holdingPeriod,
    secondaryLoan,
    legacySecondaryAnnualDS,
    capitalCostsByYear,
    servicingFeeRate,
  } = inputs;

  const projections: YearProjection[] = [];
  const bankAnnualDS = calculateAnnualDebtService(
    calculateMonthlyPayment(loanAmount, interestRate, amortizationYears)
  );
  // ⛔ One object, two outputs. A second note is serviced AND retired, or neither.
  const second = secondaryLoan && secondaryLoan.amount > 0 ? secondaryLoan : null;
  const secondAnnualDS = second ? secondaryNoteAnnualDebtService(second) : 0;
  const annualDebtService = bankAnnualDS
    + (second ? secondAnnualDS : (isValidNumber(legacySecondaryAnnualDS) ? legacySecondaryAnnualDS! : 0));

  for (let year = 1; year <= holdingPeriod; year++) {
    // ⭐⭐ What the year earns comes from the ONE rule the screen's year-1 metrics also read.
    const { income, expenses, noi, carryCosts } = projectNoiYear(inputs, year);
    // Capital hits cash flow only. `noi` above is deliberately untouched.
    const capitalThisYear = capitalCostsByYear?.[year - 1];
    // ⛔ ALL debt outstanding, not just the bank's - this is what the exit waterfall pays off.
    const bankLoanBalance = calculateLoanBalance(loanAmount, interestRate, amortizationYears, year);
    const secondaryLoanBalance = second ? secondaryNoteBalance(second, year) : 0;
    const loanBalance = bankLoanBalance + secondaryLoanBalance;
    /**
     * ⭐⭐ The ongoing financing fee, struck on the OPENING balance - the balance at the START of
     * this year, which is the balance after `year - 1` payments, not the closing one computed
     * above. Joseph's 7(a) proves it: year 1's $20,625 is 0.4125% of the full $5,000,000 note,
     * before a single payment has been made.
     * ⛔ It reduces CASH FLOW only. `annualDebtService` is deliberately untouched, so DSCR is too.
     */
    const openingBalance =
      calculateLoanBalance(loanAmount, interestRate, amortizationYears, year - 1)
      + (second ? secondaryNoteBalance(second, year - 1) : 0);
    const servicingFee = servicingFeeForYear(openingBalance, servicingFeeRate);
    const cashFlow = noi - annualDebtService - (isValidNumber(capitalThisYear) ? capitalThisYear : 0) - servicingFee - carryCosts;

    // Equity = Property Value (based on current NOI and going-in cap rate) - Loan Balance
    // For simplicity, we'll use purchase price appreciation based on NOI growth
    const impliedValue = purchasePrice * Math.pow(1 + incomeGrowthRate / 100, year - 1);
    const equity = impliedValue - loanBalance;

    projections.push({
      year,
      income,
      expenses,
      noi,
      debtService: annualDebtService,
      cashFlow,
      loanBalance,
      bankLoanBalance,
      secondaryLoanBalance,
      servicingFee,
      carryCosts,
      equity,
    });
  }

  return projections;
}

/**
 * Calculate sale proceeds at exit
 */
export interface ExitCapLadderRow {
  cap: number;
  salePrice: number;
  netToSeller: number;
  /** Gross sale price against the purchase price, in percent. */
  vsBasis: number;
  /** True for the deal's own exit cap - the column that must match the waterfall. */
  isCurrent: boolean;
}

/** Percentage-point offsets from the deal's own exit cap. */
export const EXIT_CAP_LADDER_STEPS = [-1, -0.5, -0.25, 0, 0.25, 0.5, 1] as const;

/**
 * The same sale priced across a range of exit caps, for the Exit tab's value table.
 *
 * DELIBERATELY A THIN WRAPPER OVER `calculateSaleProceeds`. It takes the exact inputs the
 * Exit waterfall already computed - the same `exitNOI` (which carries the NNN pro-forma
 * floor), the same exit loan balance, the same selling costs - and varies ONLY the cap. So
 * the `isCurrent` row is not a second calculation that happens to agree; it is the same call
 * with the same arguments, and it reproduces the waterfall by construction.
 *
 * ⚠️ This exists as a pure function specifically so it can be TESTED. `InvestmentAnalysis.tsx`
 * has no tests and cannot have any - `vitest.config.ts` sets environment 'node' and includes
 * only `*.test.ts`, so a component test would never even be collected (backlog B14). Logic
 * left inline in that file is logic nothing can check. Keep it here.
 *
 * ⚠️ An orphaned `SensitivityTable.tsx` used to compute a ladder its own way, calling
 * `generateProjections` with hardcoded 3% income growth, 2% expense growth, 2% selling costs
 * and a 6.5% exit cap - a fifth pricing path ignoring sizing_mode, seller carryback, the
 * lease schedule and rollover. It was never mounted, and was deleted rather than wired up,
 * because it would have contradicted the waterfall printed directly above it.
 *
 * Steps are absolute points around the deal's OWN cap, not a rounded base, so the middle
 * column is always exactly the deal on screen. Caps at or below zero are dropped - a
 * non-positive cap rate has no meaningful value.
 */
export function buildExitCapLadder(input: {
  exitNOI: number;
  exitCapRate: number;
  loanBalance: number;
  sellingCostsPercent: number;
  purchasePrice: number;
}): ExitCapLadderRow[] {
  const { exitNOI, exitCapRate, loanBalance, sellingCostsPercent, purchasePrice } = input;
  if (!(exitNOI > 0) || !(exitCapRate > 0)) return [];

  return EXIT_CAP_LADDER_STEPS
    .map((delta) => Number((exitCapRate + delta).toFixed(2)))
    .filter((cap) => cap > 0)
    .map((cap) => {
      const s = calculateSaleProceeds(exitNOI, cap, loanBalance, sellingCostsPercent);
      return {
        cap,
        salePrice: s.salePrice,
        netToSeller: s.netToSeller,
        vsBasis: purchasePrice > 0 ? ((s.salePrice - purchasePrice) / purchasePrice) * 100 : 0,
        isCurrent: Math.abs(cap - exitCapRate) < 0.005,
      };
    });
}

/**
 * ⭐⭐⭐ THE SALE PRICE, AND THE TWO BASES IT CAN COME FROM.
 *
 * An income property's exit is a YIELD: NOI divided by a cap rate. **LAND'S IS NOT, and it cannot
 * be** - a cap rate on zero NOI is undefined, so dividing by one prices every land exit at nothing.
 * Joseph's San Jacinto analysis reads gross sale $0, IRR -100.00% and NO GO in the app against his
 * own 0.01% and 1.00x, and the sentinel prints as a measurement.
 *
 * ⛔⛔ ONE INPUT, NOT THREE. He prices a land exit THREE ways - appreciation (a goal-seek on the
 * rate), residual (what a builder can pay for the dirt), and entitlement ($ per ENTITLED UNIT) -
 * and **which one applies depends on what the buyer intends to do.** But all three produce the
 * same KIND of answer: a VALUE. So the app needs one slot for a stated value, and **which method
 * produced it stays the user's to decide.** Building three fields would be picking his answer
 * for him with extra steps.
 *
 * ⛔ A stated value OUTRANKS the cap rate outright; it does not blend with it and it is not a
 * floor. Absent, null, zero or non-finite means "price it as a yield", which is exactly the
 * behaviour every existing analysis was saved under.
 */
export type ExitPriceBasis = 'stated_value' | 'cap_rate';

export function resolveExitSalePrice(params: {
  /** A sale VALUE stated outright. Whichever method produced it, it is the price. */
  statedExitValue?: number | null;
  exitNOI: number;
  exitCapRate: number;
}): { salePrice: number; basis: ExitPriceBasis } {
  const stated = params.statedExitValue;
  if (isValidNumber(stated) && stated > 0) {
    return { salePrice: stated, basis: 'stated_value' };
  }
  return {
    salePrice: calculateValueFromCapRate(params.exitNOI, params.exitCapRate),
    basis: 'cap_rate',
  };
}

/**
 * ⛔ THE FIFTH ARGUMENT IS OPTIONAL AND DEFAULTS TO THE YIELD PATH, so every existing call and
 * every saved analysis is untouched. Every surface that prices an exit calls THIS function, which
 * is why the stated value reaches all four of them by being added here once.
 */
export function calculateSaleProceeds(
  exitNOI: number,
  exitCapRate: number,
  loanBalance: number,
  sellingCostsPercent: number,
  statedExitValue?: number | null
): {
  salePrice: number;
  sellingCosts: number;
  netSaleProceeds: number;
  loanPayoff: number;
  netToSeller: number;
  /** Which basis priced this sale - so a surface can SAY so rather than leave it inferred. */
  exitPriceBasis: ExitPriceBasis;
} {
  const { salePrice, basis } = resolveExitSalePrice({ statedExitValue, exitNOI, exitCapRate });
  const sellingCosts = salePrice * (sellingCostsPercent / 100);
  const netSaleProceeds = salePrice - sellingCosts;
  const netToSeller = netSaleProceeds - loanBalance;

  return {
    salePrice,
    sellingCosts,
    netSaleProceeds,
    loanPayoff: loanBalance,
    netToSeller,
    exitPriceBasis: basis,
  };
}

/**
 * Calculate Internal Rate of Return (IRR) using Newton-Raphson method
 *
 * IRR is the discount rate that makes NPV = 0
 * NPV = Σ (Cash Flow_t / (1 + IRR)^t) = 0
 */
export function calculateIRR(cashFlows: number[], maxIterations = 100, tolerance = 0.0001): number {
  // cashFlows[0] = initial investment (negative)
  // cashFlows[1..n] = annual cash flows
  // cashFlows[n] includes sale proceeds

  if (cashFlows.length < 2) return 0;
  if (cashFlows[0] >= 0) return 0; // Initial investment should be negative

  // Helper: compute NPV at a given rate
  const npvAt = (rate: number): number => {
    let npv = 0;
    for (let t = 0; t < cashFlows.length; t++) {
      npv += cashFlows[t] / Math.pow(1 + rate, t);
    }
    return npv;
  };

  // Initial guess based on total return
  const totalCF = cashFlows.reduce((s, cf) => s + cf, 0);
  const totalReturn = totalCF / Math.abs(cashFlows[0]);
  let irr = Math.min(Math.max(totalReturn / cashFlows.length, -0.5), 0.5);

  // Try Newton-Raphson first (fast when it works)
  let converged = false;
  for (let i = 0; i < maxIterations; i++) {
    let npv = 0;
    let npvDerivative = 0;

    for (let t = 0; t < cashFlows.length; t++) {
      const discountFactor = Math.pow(1 + irr, t);
      npv += cashFlows[t] / discountFactor;
      if (t > 0) {
        npvDerivative -= (t * cashFlows[t]) / Math.pow(1 + irr, t + 1);
      }
    }

    if (Math.abs(npv) < tolerance) {
      converged = true;
      break;
    }

    if (npvDerivative === 0) break;
    const newIrr = irr - npv / npvDerivative;

    if (newIrr < -0.99) irr = -0.99;
    else if (newIrr > 10) irr = 10;
    else irr = newIrr;
  }

  if (converged) return irr * 100;

  // Fallback: bisection method (slower but always converges for monotonic NPV)
  // Find bounds where NPV changes sign
  let lo = -0.95;
  let hi = 10.0;
  const npvLo = npvAt(lo);
  const npvHi = npvAt(hi);

  // If NPV is negative at both bounds, IRR doesn't exist in practical range
  if (npvLo < 0 && npvHi < 0) return -100;
  // If NPV is positive at both bounds, return upper bound
  if (npvLo > 0 && npvHi > 0) return hi * 100;

  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    const npvMid = npvAt(mid);
    if (Math.abs(npvMid) < tolerance) return mid * 100;
    // NPV decreases as rate increases (for normal cash flow patterns)
    if (npvMid > 0) lo = mid;
    else hi = mid;
  }

  return ((lo + hi) / 2) * 100;
}

/**
 * Calculate Equity Multiple
 * Total distributions / Total invested capital
 */
export function calculateEquityMultiple(
  totalCashInvested: number,
  totalCashFlows: number,
  netSaleProceeds: number
): number {
  if (totalCashInvested === 0) return 0;
  return (totalCashFlows + netSaleProceeds) / totalCashInvested;
}

/**
 * Build complete IRR cash flow array for a deal
 */
export function buildIRRCashFlows(
  initialInvestment: number,
  projections: YearProjection[],
  netSaleProceeds: number
): number[] {
  const cashFlows: number[] = [-initialInvestment]; // Year 0 (negative)

  for (let i = 0; i < projections.length; i++) {
    let yearCashFlow = projections[i].cashFlow;

    // Add sale proceeds to final year
    if (i === projections.length - 1) {
      yearCashFlow += netSaleProceeds;
    }

    cashFlows.push(yearCashFlow);
  }

  return cashFlows;
}

export interface UnleveredParams {
  purchasePrice: number;
  closingCosts: number;
  capex: number;
  noiByYear: number[];
  exitGrossSale: number;
  sellingCostsPercent: number;
}

/**
 * Unlevered cash flows: what the asset itself returns, with no debt anywhere.
 * No loan proceeds at t0 and no loan payoff at exit.
 */
export function buildUnleveredCashFlows(p: UnleveredParams): number[] {
  const flows: number[] = [-(p.purchasePrice + p.closingCosts + p.capex)];
  const netSale = p.exitGrossSale * (1 - p.sellingCostsPercent / 100);
  p.noiByYear.forEach((noi, i) => {
    flows.push(i === p.noiByYear.length - 1 ? noi + netSale : noi);
  });
  return flows;
}

export interface AttributionParams {
  leveredIrr: number;
  unleveredIrr: number;
  cumulativeCashFlow: number;
  netEquityProceeds: number;
  equityInvested: number;
}

export interface AttributionResult {
  /** Percentage POINTS of IRR (levered minus unlevered). */
  leverageSpread: number | null;
  /** Dollars. */
  totalProfit: number;
  /** Share of total profit as a 0-100 PERCENTAGE. Sums to 100 with fromExit. */
  fromOperations: number | null;
  /** Share of total profit as a 0-100 PERCENTAGE. Sums to 100 with fromOperations. */
  fromExit: number | null;
}

/**
 * Deliberately simple and defensible. A decomposition that cannot be explained
 * to a client in one sentence is worse than no decomposition.
 *
 * Shares are returned as 0-100 percentages, the same convention as every other
 * percentage in this file, so `formatPercent` renders them correctly.
 */
export function attributeReturns(p: AttributionParams): AttributionResult {
  const totalProfit = p.cumulativeCashFlow + p.netEquityProceeds - p.equityInvested;
  const leverageSpread = p.leveredIrr - p.unleveredIrr;
  if (totalProfit <= 0) {
    return { leverageSpread, totalProfit, fromOperations: null, fromExit: null };
  }
  return {
    leverageSpread,
    totalProfit,
    fromOperations: (p.cumulativeCashFlow / totalProfit) * 100,
    fromExit: ((p.netEquityProceeds - p.equityInvested) / totalProfit) * 100,
  };
}

// ============================================================================
// Investment Decision Engine - Strategy Thresholds & Goal-Seek
// ============================================================================

export type InvestmentStrategy = 'core' | 'core_plus' | 'value_add' | 'opportunistic';

export interface StrategyThresholds {
  cap_rate: number | null;
  cash_on_cash: number;
  irr: number;
  equity_multiple: number;
  dscr: number;
}

export const STRATEGY_THRESHOLDS: Record<InvestmentStrategy, StrategyThresholds> = {
  core: { cap_rate: 6.50, cash_on_cash: 8.50, irr: 12.0, equity_multiple: 1.60, dscr: 1.30 },
  core_plus: { cap_rate: 6.00, cash_on_cash: 7.00, irr: 10.0, equity_multiple: 1.50, dscr: 1.20 },
  value_add: { cap_rate: 5.75, cash_on_cash: 8.75, irr: 15.0, equity_multiple: 1.80, dscr: 1.25 },
  opportunistic: { cap_rate: null, cash_on_cash: 9.50, irr: 18.0, equity_multiple: 2.00, dscr: 1.20 },
};

/**
 * Auto-classify investment strategy based on property characteristics.
 * Core: Stabilized, high-credit tenants, 90%+ occupancy, newer buildings
 * Core-Plus: Stabilized with minor upside (lease-up, small vacancy), strong anchors
 * Value-Add: Significant vacancy (< 80%), below-market rents, deferred maintenance
 * Opportunistic: Major repositioning, < 60% occupied, land, redevelopment
 */
export function classifyInvestmentStrategy(params: {
  occupancyRate?: number;
  propertyType?: string;
  yearBuilt?: number;
  tenantCredit?: string; // 'national' | 'regional' | 'local'
}): InvestmentStrategy {
  const occ = params.occupancyRate ?? 0;
  const age = params.yearBuilt ? new Date().getFullYear() - params.yearBuilt : 50;
  const hasNationalTenant = params.tenantCredit === 'national';

  // Opportunistic: very low occupancy, land, or major repositioning needed
  if (occ < 60 || params.propertyType === 'land') return 'opportunistic';

  // Value-Add: significant vacancy or older building needing work
  if (occ < 80) return 'value_add';

  // Core: high occupancy + strong tenants + newer building
  if (occ >= 95 && hasNationalTenant && age < 20) return 'core';

  // Core-Plus: stabilized with minor upside potential
  // Covers: 80-95% occupied, anchored centers, older stabilized assets
  if (occ >= 80) {
    if (hasNationalTenant || occ >= 90) return 'core_plus';
    return 'value_add';
  }

  return 'value_add';
}

/**
 * NPV = Σ (CF_t / (1 + r)^t)
 */
export function calculateNPV(cashFlows: number[], discountRate: number): number {
  let npv = 0;
  for (let t = 0; t < cashFlows.length; t++) {
    npv += cashFlows[t] / Math.pow(1 + discountRate / 100, t);
  }
  return npv;
}

/**
 * Average Cash-on-Cash return across holding period
 */
export function calculateAvgCashOnCash(projections: YearProjection[], totalCashInvested: number): number {
  if (totalCashInvested === 0 || projections.length === 0) return 0;
  const totalCoC = projections.reduce((sum, p) => sum + calculateCashOnCash(p.cashFlow, totalCashInvested), 0);
  return totalCoC / projections.length;
}

/**
 * Fewest cap-rate comps that may produce a suggestion (B1).
 *
 * ⚠️ **Must stay in lockstep with `EXIT_CAP_MIN_SAMPLE` in the backend's
 * `exitCapPoolService.ts`.** Same pairing as `normalizeAddressKey` (JS) and
 * `normalize_address` (SQL): two copies of one rule, in two languages, that disagree silently
 * if only one is changed.
 */
export const EXIT_CAP_MIN_SAMPLE = 5;

/**
 * Suggest exit cap rate from comparable sales - median of comp cap rates.
 *
 * ⚠️ The floor is the point of this function, not a detail. The exit cap prices the largest
 * single number in the whole model - the sale at the end of the hold - and this value becomes
 * the FORM DEFAULT (`InvestmentAnalysis.tsx`, `exit_cap_rate` initial state). Without a floor,
 * **one** broker-linked comp carrying a cap rate would set a deal's exit pricing outright, and
 * a median of one is just that number wearing a statistic's clothes.
 *
 * The pool path already refused below this floor - `exitCapPoolService` returns
 * `capRate: null` AND `supporting: []`, so the client cannot recompute around it. The
 * broker-linked path called straight in here and had no floor at all. Putting it inside the
 * shared function rather than at the linked call site is deliberate: two paths computing the
 * same number by different rules is the defect class that dominates this build.
 *
 * Returns null below the floor, which every caller already handles - it is the same answer
 * they get when no comp carries a cap rate, which today is all of them (22 comps in
 * production, 0 with a cap rate), so this changes no existing deal.
 */
export function suggestExitCapRate(comps: Array<{ comp_cap_rate?: number | null }>): number | null {
  const rates = comps.map(c => c.comp_cap_rate).filter((r): r is number => r != null && r > 0);
  if (rates.length < EXIT_CAP_MIN_SAMPLE) return null;
  rates.sort((a, b) => a - b);
  const mid = Math.floor(rates.length / 2);
  return rates.length % 2 === 0 ? (rates[mid - 1] + rates[mid]) / 2 : rates[mid];
}

/**
 * Benchmark price per sqft from comps
 */
export function benchmarkPricePerSqft(comps: Array<{ comp_price_per_sqft?: number | null }>): { avg: number; min: number; max: number } | null {
  const prices = comps.map(c => c.comp_price_per_sqft).filter((p): p is number => p != null && p > 0);
  if (prices.length === 0) return null;
  return {
    avg: prices.reduce((s, p) => s + p, 0) / prices.length,
    min: Math.min(...prices),
    max: Math.max(...prices),
  };
}

// ============================================================================
// Goal-Seek Functions (binary search to hit target IRR)
// ============================================================================

/** ⭐ Extends the NOI inputs, so the solver carries the lease-up and Prop 13 the screen prices (JJ2). */
export interface GoalSeekInputs extends NoiProjectionInputs {
  purchasePrice: number;
  initialNOI: number;
  incomeGrowthRate: number;
  expenseGrowthRate: number;
  initialIncome: number;
  initialExpenses: number;
  loanAmount: number;
  interestRate: number;
  amortizationYears: number;
  holdingPeriod: number;
  exitCapRate: number;
  sellingCostsPercent: number;
  totalCashInvested: number;
  closingCostsPercent: number;
  ltvPercent: number;
  buildingSqft: number;
  expenseRatio: number;
  /**
   * Contractual NOI by year, index 0 = year 1. Without this the solvers strike
   * their ceilings against a flatter income path than the four display paths,
   * which on a lease-up deal is not a rounding difference: El Paseo models
   * 1.21% IRR here against 16.73% on screen.
   */
  noiOverrides?: NoiSchedule | null;
  /**
   * Phase 3B leasing capital by year. Must reach the solver: without it goal-seek
   * strikes its ceilings against a deal that pays no TI/LC, which is the same class of
   * divergence the noiOverrides comment above describes.
   */
  capitalCostsByYear?: number[];
  /** The exit-year NOI the display paths price the exit on (an OM's stated stabilized figure). */
  exitNOIOverride?: number;
  /**
   * The deal's capital structure. `computeDealIRR` re-derives the loan on every
   * binary-search probe, so without this it silently reverts an equity-first
   * deal to LTV-driven sizing at a different leverage than the screen shows.
   */
  sizingMode?: string | null;
  /** Fixed cash equity for `equity_first` sizing. */
  downPaymentTarget?: number | null;
  /** Seller carryback annual debt service, omitted from the solvers until now. */
  /** The SECOND note, if the deal carries one. Serviced AND retired - see `ProjectionInputs`. */
  secondaryLoan?: SecondaryNote;
  /**
   * @deprecated PAYMENT ONLY - it services a second note and CANNOT retire one, because a payment
   * carries no terms to amortize. Kept solely so the pinned `goldenDeals.test.ts` and the scenario
   * tests compile unchanged; every live path passes `secondaryLoan` instead. Ignored whenever
   * `secondaryLoan` is present.
   */
  sellerAnnualDS?: number;
  /**
   * ⭐⭐ The RESOLVED financing fee, not the raw fields. The solver strikes its ceilings against
   * the same deal the verdict card scored, or the optimization table reports ceilings for a
   * different one - and a fee that raises cash by $40,990 moves every one of them. Absent is
   * exactly the previous arithmetic. ▶ `financingFee.ts`.
   */
  financingFee?: FinancingFeeEffect;
  /**
   * ⭐ A stated exit VALUE, which outranks the cap rate. Land has no NOI to divide, so without
   * this the solver and the scenario cards price every land exit at ZERO while the screen shows
   * a real number - one deal priced two ways, the defect class this build produces most.
   */
  statedExitValue?: number | null;
  /** ⭐ Route the modelled expenses to CARRY rather than NOI - see `ProjectionInputs`. */
  expensesAreCarry?: boolean;
}

/**
 * Helper: compute IRR for a given set of deal parameters.
 *
 * This is the fifth surface that prices a deal, alongside the component's
 * `generateProjections`, `computeScenario`/`buildScenarioSet` and `pdfExport`.
 * It must model the deal identically to all four or the optimization table
 * reports ceilings for a different deal than the one on screen. Three ways it
 * used to diverge, all fixed here:
 *   1. it ignored `noiOverrides`, flattening a contractual rent schedule;
 *   2. it re-derived the exit from growth, ignoring a stated exit NOI;
 *   3. it re-derived the loan from `ltvPercent` on every probe, which silently
 *      reverted an equity-first deal to LTV-driven leverage.
 * Seller carryback debt service is now charged as well.
 */
export function computeDealIRR(params: GoalSeekInputs & {
  closingCostsPercent: number;
}): number {
  // Size the loan the same way InvestmentAnalysis does. The binary searches vary
  // purchasePrice, so this must be recomputed per probe rather than reusing the
  // caller's loanAmount - but it must use the caller's SIZING RULE, not a
  // hard-coded LTV.
  let loanAmt: number;
  if (params.sizingMode === 'all_cash') {
    loanAmt = 0;
  } else if (params.sizingMode === 'equity_first' && (params.downPaymentTarget ?? 0) > 0) {
    loanAmt = sizeLoanFromEquity(params.purchasePrice, params.downPaymentTarget).loanAmount;
  } else {
    loanAmt = params.purchasePrice * (params.ltvPercent / 100);
  }

  const rawSecond = params.secondaryLoan && params.secondaryLoan.amount > 0 ? params.secondaryLoan : undefined;
  /**
   * ⭐⭐ A FINANCED FEE RIDES INTO A NOTE HERE TOO, not only on the screen. It raises the note and
   * therefore the debt service, the exit payoff and every probe's IRR; charged to cash instead, it
   * raises `totalCash`. Both go through the same resolved object, so the solver and the verdict
   * card cannot disagree about which mechanic applies. ▶ `financingFee.ts`.
   */
  const ff = params.financingFee;
  const secondaryUplift = ff?.secondaryUplift ?? 0;
  const second = rawSecond && secondaryUplift > 0
    ? { ...rawSecond, amount: rawSecond.amount + secondaryUplift }
    : rawSecond;

  /**
   * ⛔ THE DOWN PAYMENT IS STRUCK ON THE NOTE **NET** OF A FINANCED FEE, so it does not move:
   * the extra loan proceeds pay the fee, not the seller. Only the CASH cost is added below.
   * ⚠️ This solver has never netted a seller carryback out of its down payment either - a
   * pre-existing divergence from the screen, recorded in the backlog rather than fixed as a side
   * effect here, because fixing it would move pinned goldens on a deal nobody asked about.
   */
  const downPmt = params.purchasePrice - loanAmt;
  const closingCosts = params.purchasePrice * (params.closingCostsPercent / 100);
  const totalCash = calculateTotalCashRequired(downPmt, closingCosts, ff?.cashCost ?? 0);
  // The financed uplift rides into the note AFTER the down payment is struck, so it reaches the
  // debt service and the exit payoff without touching the equity.
  loanAmt += ff?.primaryUplift ?? 0;

  // ⛔ The legacy payment-only field still services the deal when no note is supplied, so the
  // pinned tests keep their meaning; it can never retire anything.
  const legacySellerDS = !second && isValidNumber(params.sellerAnnualDS) ? params.sellerAnnualDS! : 0;

  const projections = generateProjections({
    // ⭐⭐ JJ2: the NOI inputs through the one list - this used to omit the lease-up and Prop 13.
    ...pickNoiInputs(params),
    purchasePrice: params.purchasePrice,
    loanAmount: loanAmt,
    interestRate: params.interestRate,
    amortizationYears: params.amortizationYears,
    holdingPeriod: params.holdingPeriod,
    exitCapRate: params.exitCapRate,
    sellingCosts: params.sellingCostsPercent,
    secondaryLoan: second,
    legacySecondaryAnnualDS: legacySellerDS,
    capitalCostsByYear: params.capitalCostsByYear,
    // ⭐ The ongoing fee reaches the solver's cash flows too, or every ceiling is struck against
    // a deal that does not pay it. NULL is exactly the previous arithmetic.
    servicingFeeRate: ff?.servicingFeeRate ?? null,
  });

  if (projections.length === 0) return 0;

  const lastYear = projections[projections.length - 1];
  // Forward-year NOI for exit valuation. A stated exit NOI outranks the modelled
  // one, exactly as computeScenario does - otherwise the goal-seek prices the
  // exit differently from the verdict card for the same deal.
  const modelledFwdNOI = (lastYear.income * (1 + params.incomeGrowthRate / 100))
    - (lastYear.expenses * (1 + params.expenseGrowthRate / 100));
  const exitNOI = isValidNumber(params.exitNOIOverride) ? params.exitNOIOverride : modelledFwdNOI;

  const saleProceeds = calculateSaleProceeds(
    exitNOI, params.exitCapRate, lastYear.loanBalance, params.sellingCostsPercent,
    params.statedExitValue
  );
  const cashFlows = buildIRRCashFlows(totalCash, projections, saleProceeds.netToSeller);
  return calculateIRR(cashFlows);
}

/**
 * Max purchase price to achieve target IRR (binary search).
 * Higher price → lower IRR (monotonically decreasing).
 * Returns 0 if target IRR is unachievable even at the lowest price.
 */
export function solveMaxPurchasePrice(targetIRR: number, inputs: GoalSeekInputs): number {
  // Check if the target is achievable at all: even at a very low price
  const bestCaseIRR = computeDealIRR({ ...inputs, purchasePrice: inputs.purchasePrice * 0.1 });
  if (bestCaseIRR < targetIRR) return 0; // unachievable

  let lo = inputs.purchasePrice * 0.1;
  let hi = inputs.purchasePrice * 3;

  // Verify monotonicity: IRR at hi should be below target
  const worstCaseIRR = computeDealIRR({ ...inputs, purchasePrice: hi });
  if (worstCaseIRR >= targetIRR) return Math.round(hi); // even 3x price works (unlikely)

  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    const irr = computeDealIRR({ ...inputs, purchasePrice: mid });
    if (irr > targetIRR) lo = mid;
    else hi = mid;
  }

  const result = Math.round((lo + hi) / 2);
  // Sanity: verify the result actually produces an IRR near the target
  const verifyIRR = computeDealIRR({ ...inputs, purchasePrice: result });
  if (Math.abs(verifyIRR - targetIRR) > 5) return 0; // solver didn't converge properly
  return result;
}

/**
 * Required NOI lift (stabilized NOI) to achieve target IRR.
 * Higher income → higher IRR (monotonically increasing).
 * Returns 0 if unachievable.
 */
export function solveRequiredNOILift(targetIRR: number, inputs: GoalSeekInputs): number {
  let lo = inputs.initialExpenses; // minimum income = expenses (NOI=0)
  let hi = inputs.initialIncome * 5;

  // Check if target is achievable even at max income
  const bestCaseIRR = computeDealIRR({ ...inputs, initialIncome: hi });
  if (bestCaseIRR < targetIRR) return 0;

  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    const irr = computeDealIRR({ ...inputs, initialIncome: mid });
    if (irr < targetIRR) lo = mid;
    else hi = mid;
  }
  const requiredIncome = (lo + hi) / 2;
  const requiredNOI = requiredIncome - inputs.initialExpenses;
  return requiredNOI > 0 ? requiredNOI : 0;
}

/**
 * Required rent PSF to hit target NOI
 */
export function solveRequiredRentPSF(targetNOI: number, sqft: number, expenseRatio: number): number {
  if (sqft === 0) return 0;
  // NOI = Income * (1 - expenseRatio/100)
  // Income = NOI / (1 - expenseRatio/100)
  // RentPSF = Income / sqft
  const factor = 1 - expenseRatio / 100;
  if (factor <= 0) return 0;
  return (targetNOI / factor) / sqft;
}

/**
 * CapEx ceiling to achieve target IRR.
 * Higher capex → lower IRR (monotonically decreasing).
 * Returns 0 if the deal can't hit target IRR even with zero capex.
 */
export function solveCapexCeiling(targetIRR: number, inputs: GoalSeekInputs): number {
  // First check: can we hit target IRR with zero capex?
  const zeroCapeIRR = computeDealIRR({ ...inputs });
  if (zeroCapeIRR < targetIRR) return 0; // can't hit target even with $0 capex

  let lo = 0;
  let hi = inputs.purchasePrice;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    const adjustedClosingPercent = inputs.closingCostsPercent + (mid / inputs.purchasePrice) * 100;
    const irr = computeDealIRR({
      ...inputs,
      closingCostsPercent: adjustedClosingPercent,
    });
    if (irr > targetIRR) lo = mid;
    else hi = mid;
  }
  return Math.round((lo + hi) / 2);
}

// ============================================================================
// Sensitivity Analysis - Scenario Engine
// ============================================================================

/** ⭐ Extends the NOI inputs (income and expenses come from `pgi` and `totalExpenses`), so the scenario cards carry the lease-up and Prop 13 the screen prices (JJ2). */
export interface ScenarioInputs extends Omit<NoiProjectionInputs, 'initialIncome' | 'initialExpenses'> {
  purchasePrice: number;
  pgi: number;
  vacancyRate: number;
  otherIncome: number;
  nnnReimbursements: number;
  totalExpenses: number;
  ltvPercent: number;
  interestRate: number;
  amortizationYears: number;
  closingCostsPercent: number;
  vaTotalCost: number;
  incomeGrowthRate: number;
  expenseGrowthRate: number;
  holdingPeriod: number;
  exitCapRate: number;
  sellingCostsPercent: number;
  /** The SECOND note, if the deal carries one. Serviced AND retired - see `ProjectionInputs`. */
  secondaryLoan?: SecondaryNote;
  /**
   * @deprecated PAYMENT ONLY - it services a second note and CANNOT retire one, because a payment
   * carries no terms to amortize. Kept solely so the pinned `goldenDeals.test.ts` and the scenario
   * tests compile unchanged; every live path passes `secondaryLoan` instead. Ignored whenever
   * `secondaryLoan` is present.
   */
  sellerAnnualDS?: number;
  /**
   * ⭐⭐ The RESOLVED financing fee, not the raw fields. Without it the scenario cards price a
   * deal whose cash requirement differs from the verdict card's by the whole fee, and report a
   * different IRR for the same deal on the same screen. ▶ `financingFee.ts`.
   */
  financingFee?: FinancingFeeEffect;
  /**
   * ⭐ A stated exit VALUE, which outranks the cap rate. Land has no NOI to divide, so without
   * this the solver and the scenario cards price every land exit at ZERO while the screen shows
   * a real number - one deal priced two ways, the defect class this build produces most.
   */
  statedExitValue?: number | null;
  /** ⭐ Route the modelled expenses to CARRY rather than NOI - see `ProjectionInputs`. */
  expensesAreCarry?: boolean;
  /**
   * Exit-year NOI stated by the source document rather than modelled from
   * growth. An NNN deal's exit is valued on the OM's contractual stabilized
   * NOI, and the summary path already does that. Without this field the
   * scenario engine re-derives a forward NOI from incomeGrowthRate and prices
   * the exit differently, so the verdict card and the scenario cards report
   * two different IRRs for one deal on one screen.
   */
  exitNOIOverride?: number;
  /**
   * Contractual NOI schedule for the deal. Without this the scenario engine
   * silently re-derives NOI from incomeGrowthRate and reports a different IRR
   * than the summary path for the same deal.
   */
  noiOverrides?: NoiSchedule | null;
  /** Phase 3B leasing capital by year, for the same reason. */
  capitalCostsByYear?: number[];
}

export interface ScenarioResult {
  irr: number;
  avgCoC: number;
  dscr: number;
  equityMultiple: number;
  noi: number;
  cashFlow: number;
}

/**
 * Recompute return metrics with percentage adjustments to purchase price and/or PGI.
 * priceMod and pgiMod are percentage offsets (e.g. -10 means -10%).
 */
export function computeScenario(
  base: ScenarioInputs,
  priceMod: number,
  pgiMod: number
): ScenarioResult {
  const pp = base.purchasePrice * (1 + priceMod / 100);
  const pgi = base.pgi * (1 + pgiMod / 100);

  const vacancyAmount = calculateVacancyAmount(pgi, base.vacancyRate);
  const egi = calculateEffectiveGrossIncome(pgi, vacancyAmount, base.otherIncome + base.nnnReimbursements);
  /**
   * ⭐⭐ JJ2: YEAR 1 THROUGH THE ONE RULE - the schedule, the lease-up and the carry included. This
   * used to strike `egi - totalExpenses` itself, so the scenario cards printed DSCR -0.29x beside a
   * headline of 0.00x on the carried Victorville deal.
   */
  const noiIn = pickNoiInputs({ ...base, initialIncome: egi, initialExpenses: base.totalExpenses });
  const yearOne = projectNoiYear(noiIn, 1);
  const noival = yearOne.noi;

  /**
   * ⭐⭐ The RESOLVED financing fee, applied here exactly as the verdict card applies it: a
   * financed fee raises a NOTE (and so the debt service and the exit payoff), a cash fee raises
   * TOTAL CASH. Omit it and the scenario cards report a different IRR for the same deal on the
   * same screen - which is the divergence the exit-NOI override was added to close.
   */
  const ff = base.financingFee;
  const primaryUplift = ff?.primaryUplift ?? 0;
  const secondaryUplift = ff?.secondaryUplift ?? 0;
  /** The purchase-money loan, before any financed fee rides into it - what the equity is struck on. */
  const purchaseMoneyLoan = calculateLoanAmount(pp, base.ltvPercent);
  const loan = purchaseMoneyLoan + primaryUplift;
  const monthlyPmt = calculateMonthlyPayment(loan, base.interestRate, base.amortizationYears);
  const bankDS = calculateAnnualDebtService(monthlyPmt);
  const rawSecond = base.secondaryLoan && base.secondaryLoan.amount > 0 ? base.secondaryLoan : undefined;
  const scenarioSecond = rawSecond && secondaryUplift > 0
    ? { ...rawSecond, amount: rawSecond.amount + secondaryUplift }
    : rawSecond;
  const scenarioSellerDS = scenarioSecond
    ? secondaryNoteAnnualDebtService(scenarioSecond)
    : (isValidNumber(base.sellerAnnualDS) ? base.sellerAnnualDS! : 0);
  const totalDS = bankDS + scenarioSellerDS;

  // ⛔ Struck on the purchase-money loan, so a financed fee does not move the equity - see
  // `computeDealIRR`. ⚠️ This path has never netted a second note out of its down payment either;
  // that divergence is recorded in the backlog, not fixed as a side effect here.
  const downPmt = pp - purchaseMoneyLoan;
  const closing = calculateClosingCosts(pp, base.closingCostsPercent);
  const totalCash = calculateTotalCashRequired(downPmt > 0 ? downPmt : 0, closing, ff?.cashCost ?? 0) + base.vaTotalCost;

  const dscrVal = calculateDSCR(noival, totalDS);
  // ⛔ A carry reduces cash flow and never NOI - deducted here exactly as the projection's year 1 does.
  const cfYear1 = calculateBeforeTaxCashFlow(noival, totalDS) - yearOne.carryCosts;

  const projections = generateProjections({
    ...noiIn,
    purchasePrice: pp,
    loanAmount: loan,
    interestRate: base.interestRate,
    amortizationYears: base.amortizationYears,
    holdingPeriod: base.holdingPeriod,
    exitCapRate: base.exitCapRate,
    sellingCosts: base.sellingCostsPercent,
    secondaryLoan: scenarioSecond,
    legacySecondaryAnnualDS: scenarioSecond ? undefined : scenarioSellerDS,
    capitalCostsByYear: base.capitalCostsByYear,
    servicingFeeRate: ff?.servicingFeeRate ?? null,
  });

  if (projections.length === 0) {
    return { irr: 0, avgCoC: 0, dscr: dscrVal, equityMultiple: 0, noi: noival, cashFlow: cfYear1 };
  }

  const lastYear = projections[projections.length - 1];
  // Forward-year NOI for exit valuation. This is driven by the overridden final
  // year whenever one exists, because generateProjections back-solves income
  // from the contractual NOI (income = noi + expenses) - so growing income here
  // grows the contractual figure, not a stale growth-rate one. Break that
  // back-solve and the exit sale silently reverts to the modelled NOI.
  // A stated exit NOI (an OM's contractual stabilized figure) outranks the
  // modelled one. The summary path already prefers it for NNN deals, so
  // ignoring it here is what makes the verdict card and the scenario cards
  // disagree about the same deal.
  const modelledFwdNOI = (lastYear.income * (1 + base.incomeGrowthRate / 100))
    - (lastYear.expenses * (1 + base.expenseGrowthRate / 100));
  const fwdNOI = isValidNumber(base.exitNOIOverride) ? base.exitNOIOverride : modelledFwdNOI;
  const sale = calculateSaleProceeds(fwdNOI, base.exitCapRate, lastYear.loanBalance, base.sellingCostsPercent, base.statedExitValue);
  const irrCFs = buildIRRCashFlows(totalCash, projections, sale.netToSeller);
  const irr = calculateIRR(irrCFs);
  const totalCFs = projections.reduce((s, p) => s + p.cashFlow, 0);
  const eqMult = calculateEquityMultiple(totalCash, totalCFs, sale.netToSeller);
  const avgCoC = calculateAvgCashOnCash(projections, totalCash);

  return { irr, avgCoC, dscr: dscrVal, equityMultiple: eqMult, noi: noival, cashFlow: cfYear1 };
}

/**
 * Target exit cap rate to achieve target IRR.
 * Lower exit cap → higher sale price → higher IRR.
 * Returns 0 if unachievable even at 1% exit cap.
 */
export function solveTargetExitCap(targetIRR: number, inputs: GoalSeekInputs): number {
  // Check if achievable at the most aggressive exit cap (1%)
  const bestCaseIRR = computeDealIRR({ ...inputs, exitCapRate: 1 });
  if (bestCaseIRR < targetIRR) return 0;

  let lo = 1;
  let hi = 15;
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    const irr = computeDealIRR({ ...inputs, exitCapRate: mid });
    if (irr > targetIRR) lo = mid;
    else hi = mid;
  }
  return parseFloat(((lo + hi) / 2).toFixed(2));
}

// ============================================================================
// Scenario Set and 2D Sensitivity Grid
// ============================================================================

export interface ScenarioDeltas {
  exitCapBps: number;      // basis points added on downside, subtracted on upside
  incomeGrowthPts: number; // percentage points
  vacancyPts: number;      // percentage points, downside only
}

export const DEFAULT_SCENARIO_DELTAS: ScenarioDeltas = {
  exitCapBps: 50,
  incomeGrowthPts: 1.0,
  vacancyPts: 3.0,
};

/**
 * Three cases side by side. Replaces "one case plus sliders", which forces the
 * reader to imagine the distribution instead of being shown it.
 *
 * The upside exit cap is floored at 0.01 so a low base cap can never produce a
 * zero or negative cap rate, which would divide by zero in the exit valuation.
 */
export function buildScenarioSet(
  base: ScenarioInputs,
  overrides: Partial<ScenarioDeltas> = {}
) {
  const d = { ...DEFAULT_SCENARIO_DELTAS, ...overrides };
  const capStep = d.exitCapBps / 100;

  const downInputs: ScenarioInputs = {
    ...base,
    exitCapRate: base.exitCapRate + capStep,
    incomeGrowthRate: base.incomeGrowthRate - d.incomeGrowthPts,
    vacancyRate: (base.vacancyRate ?? 0) + d.vacancyPts,
  };
  const upInputs: ScenarioInputs = {
    ...base,
    exitCapRate: Math.max(0.01, base.exitCapRate - capStep),
    incomeGrowthRate: base.incomeGrowthRate + d.incomeGrowthPts,
  };

  return {
    downside: { ...computeScenario(downInputs, 0, 0), exitCapUsed: downInputs.exitCapRate },
    base: { ...computeScenario(base, 0, 0), exitCapUsed: base.exitCapRate },
    upside: { ...computeScenario(upInputs, 0, 0), exitCapUsed: upInputs.exitCapRate },
  };
}

/**
 * Only the scalar inputs can be swept by the grid - assigning a number to the
 * NOI schedule would silently produce a nonsense scenario.
 */
export type NumericScenarioKey = {
  [K in keyof ScenarioInputs]-?: NonNullable<ScenarioInputs[K]> extends number ? K : never;
}[keyof ScenarioInputs];

export interface GridParams {
  base: ScenarioInputs;
  xVar: NumericScenarioKey;
  xValues: number[];
  yVar: NumericScenarioKey;
  yValues: number[];
  metric: 'irr' | 'equityMultiple' | 'avgCoC' | 'dscr';
}

/**
 * Two-variable sensitivity. Rows are yValues, columns are xValues.
 */
export function sensitivityGrid(p: GridParams): number[][] {
  return p.yValues.map(y =>
    p.xValues.map(x => {
      const inputs = { ...p.base, [p.xVar]: x, [p.yVar]: y } as ScenarioInputs;
      return computeScenario(inputs, 0, 0)[p.metric];
    })
  );
}
